import * as R from 'ramda'
import {url} from '../../App/AuthenticatedRoute'
import * as OrderFilteringState
    from '../../Components/OrderFiltering/Model/OrderFilteringState'
import {OrderFilteringFeedback} from '../../Components/OrderFiltering/OrderFilteringFeedback'
import {
    orderFilterHttpResponseAction,
    orderFilteringReducer,
} from '../../Components/OrderFiltering/OrderFilteringReducer'
import {FeedbackLoop} from '../../Data/FeedbackLoop'
import {Dictionary} from '../../Data/Dictionary'
import {buildUrl, decodeHttpResponse, HttpError} from '../../Data/Http'
import {HttpRequest} from '../../Data/HttpRequest'
import {update} from '../../Data/ObjectHelper'
import * as PageSection from '../../Data/PageSection'
import * as Pagination from '../../Data/Pagination'
import * as Result from '../../Data/Result'
import {navigate} from '../../Data/Browser'
import * as SerializedOrderFilters from '../../Data/SerializedOrderFilters'
import {Session} from '../../Data/Session'
import * as FilteredAndPaginatedOrdersWithOwners
    from '../../Entities/FilteredAndPaginatedOrdersWithOwners'
import {orderDecoder} from '../../Entities/Order'
import {OrderWithOwners} from '../../Entities/OrderWithOwners'
import * as OrderResource from '../../Request/OrderResource'
import {OrderOverviewAction} from './OrderOverviewActions'
import {
    OrderOverviewFeedback,
    OrderOverviewRequestIdentifier
} from './OrderOverviewFeedback'
import {OrderOverviewState} from './OrderOverviewState'

type OrderOverviewLoop =
    FeedbackLoop<OrderOverviewState, OrderOverviewFeedback>

export function activate(
    session: Session,
    queryParameters: Dictionary<string>,
): [HttpRequest, PageSection.M] {
    const pageSection =
        PageSection.fromQueryParamters(initialItemsPerPage, queryParameters)
    return [
        OrderResource.listWithOwners(
            session.manufacturer.id,
            SerializedOrderFilters.fromQueryParameters(queryParameters),
            PageSection.toOffsetLimit(pageSection)
        ),
        pageSection,
    ]
}

export const initialDecoder = FilteredAndPaginatedOrdersWithOwners.decoder

export function init(
    orders: FilteredAndPaginatedOrdersWithOwners.M,
    pageSection: PageSection.M,
): OrderOverviewState {
    return {
        orderFilter: OrderFilteringState.init(orders.filters),
        orders: Pagination.fromPaginated(pageSection, orders),
        error: null,
    }
}

export function refresh(
    orders: FilteredAndPaginatedOrdersWithOwners.M,
    pageSection: PageSection.M,
    state: OrderOverviewState,
): OrderOverviewState {
    return update(
        {
            orderFilter:
                OrderFilteringState.setActiveFilters(
                    orders.filters,
                    state.orderFilter,
                ),
            orders: Pagination.fromPaginated(pageSection, orders),
        },
        state,
    )
}

const initialItemsPerPage = 10

export function reduce(
    session: Session,
    action: OrderOverviewAction,
    state: OrderOverviewState,
): OrderOverviewLoop {
    switch (action.type) {
        case 'NoOp':
            return {state, feedback: []}
        case 'OrderPaidChanged':
            return {
                state,
                feedback: [{
                    type: 'SendRequest',
                    identifier: {type: 'OrderPaidPatch'},
                    httpRequest: OrderResource.patchOrderPaid(
                        action.checked ? 'Paid' : 'Unpaid',
                        action.orderId,
                    ),
                }],
            }
        case 'OrderPaidResponse': {
            const result =
                decodeHttpResponse(
                    orderDecoder(),
                    action.result,
                )
            return {
                state: update({
                    orders: update({
                        data: state.orders.data.map((orderWithOwners: OrderWithOwners) => {
                            if (result.type === 'Ok' && orderWithOwners.order.id === result.value.id) {
                                return update({
                                    order: update({
                                        orderPaid: result.value.orderPaid,
                                    }, orderWithOwners.order),
                                }, orderWithOwners)
                            } else {
                                return orderWithOwners
                            }
                        }),
                    }, state.orders),
                }, state),
                feedback: [],
            }
        }
        case 'PaginationNavigation':
            return requestOrders(
                session,
                {
                    state: update(
                        {
                            orders:
                                Pagination.navigate(
                                    action.navigation,
                                    state.orders,
                                ),
                        },
                        state
                    ),
                    feedback: []
                },
            )
        case 'MenuButtonClicked': {
            return {state, feedback: [{type: 'OpenDrawer'}]}
        }
        case 'ItemsPerPageChanged': {
            return requestOrders(
                session,
                {
                    state: update(
                        {
                            orders:
                                Pagination.setItemsPerPage(
                                    action.value,
                                    state.orders,
                                )
                        },
                        state,
                    ),
                    feedback: [],
                },
            )
        }
        case 'OrderFilteringModuleAction':
            const
                orderFilterLoop =
                    orderFilteringReducer(state.orderFilter, action.subAction),
                wrappedLoop = {
                    state: update(
                        {orderFilter: orderFilterLoop.state},
                        state,
                    ),
                    feedback: [],
                }
            return R.reduce(
                (
                    previousLoop: OrderOverviewLoop,
                    orderFilterFeedback: OrderFilteringFeedback,
                ): OrderOverviewLoop => {
                    switch (orderFilterFeedback.type) {
                        case 'SendRequest':
                            return {
                                state: previousLoop.state,
                                feedback:
                                    R.append<OrderOverviewFeedback>(
                                        {
                                            type: 'SendRequest',
                                            identifier: {
                                                type: 'OrderFilterRequest',
                                                identifier:
                                                orderFilterFeedback.identifier,
                                            },
                                            httpRequest: orderFilterFeedback.httpRequest,
                                        },
                                        previousLoop.feedback,
                                    ),
                            }
                        case 'FiltersChanged':
                            return requestOrders(session, previousLoop)
                    }
                },
                wrappedLoop,
                orderFilterLoop.feedback,
            )
    }
}

function requestOrders(
    session: Session,
    {state, feedback}: OrderOverviewLoop
): OrderOverviewLoop {
    return {
        state,
        feedback: R.append<OrderOverviewFeedback>(
            {
                type: 'RunEffect',
                effect:
                    navigate<OrderOverviewAction>(
                        buildUrl(
                            url({type: 'OrderOverview'}),
                            {
                                ...PageSection.toQueryParameters(
                                    state.orders.pageSection,
                                ),
                                ...SerializedOrderFilters.toQueryParameters(
                                    SerializedOrderFilters.fromOrderFilters(
                                        state.orderFilter.activeFilters,
                                    ),
                                ),
                            }
                            ,
                        ),
                        {type: 'NoOp'}
                    )
            },
            feedback,
        ),
    }
}

export function httpResponseAction(
    identifier: OrderOverviewRequestIdentifier,
    result: Result.Result<HttpError, string>
): OrderOverviewAction {
    switch (identifier.type) {
        case 'OrderPaidPatch':
            return {
                type: 'OrderPaidResponse',
                result,
            }
        case 'OrderFilterRequest':
            return {
                type: 'OrderFilteringModuleAction',
                subAction:
                    orderFilterHttpResponseAction(
                        identifier.identifier,
                        result,
                    ),
            }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Pages/OrderOverview/OrderOverviewReducer.ts